import { encounterCreatureField, itemsCol, posCoord } from '../shared/array-types';
import { heldEncounterForAction } from '../shared/entity-compile-ready';
import { resolveCachedItemForRender } from './find-cached-item';

type TrapWindow = Window & {
    ftGetActiveCasterId?: () => string;
    ftTryCommitTrapTrigger?: (input: { encounterId: string; actorId: string; hostInventoryId: string; targetCreatureId: string })
        => Promise<{ ok: boolean; fired: number; error?: string }>;
    startGmTargetAction?: (options: { key: string; label: string; minTargets: number; maxTargets: number;
        onConfirm: (ids: string[]) => Promise<void> }) => void;
    cancelGmTargetAction?: () => void;
};
let closePicker: (() => void) | null = null;

/** Both card and radial enter this single-target, action-owned GM picker. */
export function pickTrapTrigger(hostInventoryId: string): void {
    const page = window as TrapWindow;
    if (page.ftActiveViewerIsFullGm?.() !== true) return;
    closePicker?.();
    page.cancelGmTargetAction?.();
    const context = typeof getSidelogContext === 'function' ? getSidelogContext() : null;
    const encounterId = String(context?.encounter_id ?? '').toUpperCase();
    const encounter = heldEncounterForAction(encounterId), host = resolveCachedItemForRender(hostInventoryId);
    if (!encounter || !host || !page.ftTryCommitTrapTrigger) { displayWarning('The trap encounter is still loading.'); return; }
    const candidates = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])
        .map(row => ({ row, id: String(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase(),
            distance: Math.hypot(...(['x_pos', 'y_pos', 'z_pos'] as const).map(axis => (posCoord(row, axis) ?? 0) - (posCoord(host, axis) ?? 0))) }))
        .filter(({ id, row }) => id && !Number(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_deleted') ?? 0))
        .sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
    const fire = async (targetCreatureId: string): Promise<void> => {
        if (page.ftActiveViewerIsFullGm?.() !== true) return;
        try {
            const result = await page.ftTryCommitTrapTrigger!({ encounterId, actorId: page.ftGetActiveCasterId?.() || encounterId,
                hostInventoryId, targetCreatureId });
            if (!result.ok) displayWarning(result.error ?? 'The trap could not be triggered.');
        } catch (error) { displayWarning(String((error as Error)?.message ?? 'The trap could not be triggered.')); }
    };
    const dialog = document.createElement('dialog');
    dialog.setAttribute('aria-label', 'Trigger Trap');
    dialog.style.cssText = 'color:inherit;background:#18292d;border:1px solid #b9a66b;border-radius:12px;padding:16px;max-width:calc(100vw - 24px)';
    const title = document.createElement('h3'); title.textContent = 'Trigger Trap'; dialog.append(title);
    let retired = false;
    const retire = (): void => {
        if (retired) return;
        retired = true; closePicker = null; page.ftReleaseInteractionMode?.('gm-target-action'); dialog.remove();
    };
    const close = (): void => { dialog.close(); retire(); };
    closePicker = close;
    const add = (label: string, run: () => void): void => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'btn btn-secondary d-block mb-2';
        button.textContent = label; button.addEventListener('click', run, { once: true }); dialog.append(button);
    };
    for (const { id, row } of candidates.slice(0, 8)) {
        const name = String(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_name') ?? itemsCol(row, 'item_name') ?? 'Unnamed creature');
        add(`Trigger against ${name}`, () => { close(); void fire(id); });
    }
    add('Choose another creature…', () => {
        close();
        if (!page.startGmTargetAction) { displayWarning('The scene creature picker is still loading.'); return; }
        page.startGmTargetAction({ key: 'trap-trigger:' + hostInventoryId, label: 'Trigger Trap', minTargets: 1, maxTargets: 1,
            onConfirm: async ids => { if (ids[0]) await fire(ids[0]); } });
    });
    add('Cancel', close);
    dialog.addEventListener('close', retire, { once: true });
    page.ftClaimInteractionMode?.('gm-target-action', close);
    document.body.append(dialog); dialog.showModal();
}
